import React, { useRef } from 'react';
import {
    VideoCameraOutlineSmall,
    FloorplanSmall,
    MoneyhouseSmall,
    FlagSmall,
    ViewSmall,
    PiechartSmall,
    DocumentSmall,
    StatsSmall
} from '@blueprint/icons';

import { Heading2 } from '@blueprint/ui';
import { CTAButton, CTALink } from 'listing-details/components/CTA/CTA';
import { MoreInformationWrapper } from './ListingMoreInformation.styles';
import {
    CTAs,
    CTAType,
    ListingMoreInformationProps
} from './ListingMoreInformation.types';
import {
    trackMoreInformationCTAClick,
    trackMoreInformationClickThrough
} from './ListingMoreInformation.tracking';

import { getCTAs } from './ListingMoreInformation.helpers';
import SmartViewingImage from '../SmartViewings/SmartViewingImage';
import { ModalTypes } from '../ModalManager/ModalManager.types';
import CTAList from '../CTAList/CTAList';

const ListingMoreInformation = ({
    data,
    listingSection,
    reportListingURL,
    openModal
}: ListingMoreInformationProps) => {
    const ctaClickHandler = (
        cta: CTAType,
        triggerOrigin?: React.RefObject<HTMLElement>,
        modal?: ModalTypes
    ) => {
        return modal && triggerOrigin
            ? () => {
                  openModal(modal, triggerOrigin);
                  trackMoreInformationCTAClick(cta);
              }
            : () => {
                  trackMoreInformationClickThrough(cta);
              };
    };

    const availableCtas: CTAs = {
        floorplans: {
            type: 'button',
            origin: useRef(null),
            label: 'Floor plans',
            cta: 'floorplans',
            modal: 'floorplans',
            icon: <FloorplanSmall />,
            dataTestid: 'floorplans-button'
        },
        tours: {
            type: 'button',
            origin: useRef(null),
            label: 'Virtual tours',
            cta: 'tours',
            modal: 'tours',
            icon: <VideoCameraOutlineSmall />,
            dataTestid: 'virtualtours-button'
        },
        energy: {
            type: 'button',
            origin: useRef(null),
            label: 'Energy Performance Certificate',
            cta: 'energy',
            modal: 'energy',
            icon: <MoneyhouseSmall />,
            dataTestid: 'energyperfcert-button'
        },
        streetview: {
            type: 'button',
            origin: useRef(null),
            label: 'Street view',
            cta: 'streetview',
            modal: 'streetview',
            icon: <ViewSmall />,
            dataTestid: 'streetview-button'
        },
        smart_viewing: {
            type: 'button',
            origin: useRef(null),
            label: 'Request a guided Smart Viewing',
            cta: 'smart_viewing',
            modal: 'smartViewing',
            icon: SmartViewingImage,
            dataTestid: 'smart-viewing-button'
        },
        listing_history: {
            type: 'button',
            origin: useRef(null),
            label: 'Listing history',
            cta: 'listing_history',
            modal: 'listing_history',
            icon: <StatsSmall />,
            dataTestid: 'listinghistory-button'
        },
        market_stats: {
            type: 'button',
            origin: useRef(null),
            label: 'Market stats',
            cta: 'market_stats',
            modal: 'market_stats',
            icon: <PiechartSmall />,
            dataTestid: 'marketstats-button'
        },

        additional_links: {
            type: 'button',
            origin: useRef(null),
            label: 'Additional links',
            cta: 'additional_links',
            modal: 'additional_links',
            icon: <DocumentSmall />,
            dataTestid: 'additionallinks-button'
        },
        report_listing: {
            type: 'link',
            label: 'Report this listing',
            cta: 'report_listing',
            icon: <FlagSmall />,
            dataTestid: 'reportlisting-button',
            href: reportListingURL
        }
    };

    const requiredCtas = getCTAs(data, listingSection, availableCtas);

    return (
        <MoreInformationWrapper
            aria-labelledby="listing-more-information-heading"
            data-testid="listing-more-information"
        >
            <Heading2 size="6" id="listing-more-information-heading">
                More information
            </Heading2>

            <CTAList
                data={requiredCtas.map((cta) =>
                    cta.type === 'button' ? (
                        <CTAButton
                            ref={cta.origin}
                            label={cta.label}
                            icon={cta.icon}
                            onClick={ctaClickHandler(
                                cta.cta,
                                cta.origin,
                                cta.modal
                            )}
                            data-testid={cta.dataTestid}
                        />
                    ) : (
                        <CTALink
                            label={cta.label}
                            icon={cta.icon}
                            data-testid={cta.dataTestid}
                            onClick={ctaClickHandler(cta.cta)}
                            href={cta.href}
                        />
                    )
                )}
            />
        </MoreInformationWrapper>
    );
};

export default ListingMoreInformation;
